/**
 * User Preference API — backs `useUserPref<T>()`.
 *
 * Upsert is implemented as find-then-update-or-create (no DB alternate key
 * yet on (userid, scope, key)). The race-condition window is small and the
 * worst case is a duplicate row, which we tolerate by always taking the most
 * recently modified one on read. If users start hitting that, add an alternate
 * key on the table and switch to a real OData upsert.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dataverseClient, TABLES } from '@shared/api';
import { useAuth } from '@shared/providers/AuthProvider';
import type { UserPreference } from '../model/types';

interface UserPreferenceEntity {
  vba_userpreferenceid: string;
  vba_name?: string;
  vba_userid?: string;
  vba_scope?: string;
  vba_key?: string;
  vba_value?: string;
  modifiedon?: string;
  [k: string]: unknown;
}

function mapToDomain<T>(entity: UserPreferenceEntity): UserPreference<T> {
  let parsed: T;
  try {
    parsed = entity.vba_value ? (JSON.parse(entity.vba_value) as T) : (undefined as unknown as T);
  } catch {
    parsed = entity.vba_value as unknown as T;
  }
  return {
    id: entity.vba_userpreferenceid,
    userId: entity.vba_userid ?? '',
    scope: entity.vba_scope ?? '',
    key: entity.vba_key ?? '',
    value: parsed,
    modifiedOn: entity.modifiedon ?? undefined,
  };
}

function escape(s: string): string {
  return s.replace(/'/g, "''");
}

async function findOne<T>(
  userId: string,
  scope: string,
  key: string,
): Promise<UserPreference<T> | null> {
  const filter = [
    `vba_userid eq '${escape(userId)}'`,
    `vba_scope eq '${escape(scope)}'`,
    `vba_key eq '${escape(key)}'`,
  ].join(' and ');
  const response = await dataverseClient.list<UserPreferenceEntity>(TABLES.USER_PREFERENCE, {
    filter,
    orderBy: 'modifiedon desc',
    top: 1,
  });
  if (response.value.length === 0) return null;
  return mapToDomain<T>(response.value[0]!);
}

async function create<T>(
  userId: string,
  scope: string,
  key: string,
  value: T,
): Promise<UserPreference<T>> {
  const entity: Record<string, unknown> = {
    vba_name: `${userId.slice(0, 8)}:${scope}:${key}`,
    vba_userid: userId,
    vba_scope: scope,
    vba_key: key,
    vba_value: JSON.stringify(value),
  };
  const response = await dataverseClient.create<UserPreferenceEntity>(
    TABLES.USER_PREFERENCE,
    entity,
  );
  return mapToDomain<T>(response);
}

async function update<T>(id: string, value: T): Promise<void> {
  await dataverseClient.update(TABLES.USER_PREFERENCE, id, {
    vba_value: JSON.stringify(value),
  });
}

async function upsert<T>(
  userId: string,
  scope: string,
  key: string,
  value: T,
): Promise<UserPreference<T>> {
  const existing = await findOne<T>(userId, scope, key);
  if (existing) {
    await update(existing.id, value);
    return { ...existing, value };
  }
  return await create(userId, scope, key, value);
}

export const userPreferenceApi = {
  findOne,
  upsert,
  create,
  update,
};

// =============================================================================
// Generic hook
// =============================================================================

export interface UseUserPrefResult<T> {
  /** Loaded value, or `defaultValue` while loading or if no row exists. */
  value: T;
  /** True until the first read finishes. */
  isLoading: boolean;
  /** True if a server row exists for this (scope, key). */
  isPersisted: boolean;
  /** Async setter — writes through to the server (upsert). Returns the new value. */
  setValue: (next: T | ((prev: T) => T)) => Promise<void>;
  /** Re-fetch from server. */
  refetch: () => Promise<void>;
  /** Last server error from a read or write, if any. */
  error?: Error;
}

/**
 * Per-user preference hook.
 *
 * - Reads once on mount (and when scope/key change).
 * - Writes synchronously update local state (so the UI is snappy) then
 *   upsert to Dataverse in the background. If the write fails, the local
 *   state is rolled back to the last server value and `error` is set.
 *
 * @param scope  Logical bucket — e.g. "ops.myGroups", "ops.group.<accountId>"
 * @param key    Pref key within the scope — e.g. "view", "pinnedGI"
 * @param defaultValue Fallback returned while loading or when no row exists
 * @param options.migrateFromLocalStorage  One-shot: compute a localStorage key
 *   from the user's oid; if a value exists there, push it to Dataverse and
 *   clear it. Lets us cut over from the pre-existing localStorage-backed prefs
 *   without losing them. Receives the oid so callers that keyed by oid can
 *   construct the legacy key.
 */
export function useUserPref<T>(
  scope: string,
  key: string,
  defaultValue: T,
  options?: {
    migrateFromLocalStorage?: (oid: string) => string;
  },
): UseUserPrefResult<T> {
  const { user } = useAuth();
  const oid = user?.oid ?? '';

  const [value, setLocal] = useState<T>(defaultValue);
  const [isLoading, setLoading] = useState(true);
  const [isPersisted, setPersisted] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // Stable ref so an inline function from the caller doesn't retrigger loads.
  // Migration is one-shot per oid; we always read the latest fn from the ref.
  const migrationKeyFnRef = useRef(options?.migrateFromLocalStorage);
  migrationKeyFnRef.current = options?.migrateFromLocalStorage;

  const load = useCallback(async () => {
    if (!oid) {
      setLocal(defaultValue);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const row = await userPreferenceApi.findOne<T>(oid, scope, key);
      if (row) {
        setLocal(row.value ?? defaultValue);
        setPersisted(true);
        setError(undefined);
        setLoading(false);
        return;
      }

      // No server row — try the one-shot localStorage migration if requested.
      const migrationKeyFn = migrationKeyFnRef.current;
      if (migrationKeyFn) {
        const migrationKey = migrationKeyFn(oid);
        try {
          const raw = localStorage.getItem(migrationKey);
          if (raw) {
            const parsed = JSON.parse(raw) as T;
            await userPreferenceApi.upsert<T>(oid, scope, key, parsed);
            localStorage.removeItem(migrationKey);
            setLocal(parsed);
            setPersisted(true);
            setError(undefined);
            setLoading(false);
            return;
          }
        } catch (migErr) {
          // Migration is best-effort — log and fall through to default.
          console.warn(`useUserPref migration failed for ${migrationKey}:`, migErr);
        }
      }

      setLocal(defaultValue);
      setPersisted(false);
      setError(undefined);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setLoading(false);
    }
    // defaultValue intentionally excluded — see useEffect note below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid, scope, key]);

  useEffect(() => {
    void load();
    // We intentionally don't include `defaultValue` in deps — it's expected to
    // be a stable reference; if it changes every render the hook would refetch
    // forever. Callers should memoize complex defaults.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oid, scope, key]);

  const setValue = useCallback(
    async (next: T | ((prev: T) => T)) => {
      if (!oid) return;
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(value) : next;
      const previous = value;
      setLocal(resolved);
      try {
        await userPreferenceApi.upsert<T>(oid, scope, key, resolved);
        setPersisted(true);
        setError(undefined);
      } catch (err) {
        // Roll back optimistic update so UI matches reality. Surface the
        // underlying error to the console so the user can see *why* it
        // bounced — silent rollback is the worst diagnostic experience.
        console.error(
          `[useUserPref] write failed for ${scope}/${key} — rolling back. Error:`,
          err,
        );
        setLocal(previous);
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    },
    [oid, scope, key, value],
  );

  return useMemo(
    () => ({ value, isLoading, isPersisted, setValue, refetch: load, error }),
    [value, isLoading, isPersisted, setValue, load, error],
  );
}
